import React from 'react';
import { Modal, Typography } from 'antd';

/**
 * CustomModal - Componente base para todos los modales del sistema
 *
 * Props:
 * - open: boolean - Si el modal está abierto
 * - onCancel: function - Función para cerrar el modal
 * - title: string - Título del modal
 * - subtitle: string - Subtítulo opcional del modal
 * - icon: React.ReactNode - Icono para mostrar en el header
 * - headerColor: string - Color del gradiente del header (default: '#253289')
 * - width: number|string - Ancho del modal
 * - centered: boolean - Si el modal debe estar centrado
 * - children: React.ReactNode - Contenido del modal
 * - footer: React.ReactNode - Footer personalizado (null para sin footer)
 * - maxHeight: string - Altura máxima del contenido
 * - closable: boolean - Si muestra el botón X de cerrar
 * - maskClosable: boolean - Si se puede cerrar haciendo click fuera
 * - style: object - Estilos adicionales para el modal
 * - bodyPadding: string - Padding del body (default: '24px')
 */

const CustomModal = ({
    open,
    onCancel,
    title,
    subtitle,
    icon,
    headerColor = '#253289',
    width = 500,
    centered = true,
    children,
    footer = null,
    maxHeight,
    closable = true,
    maskClosable = true,
    style = {},
    bodyPadding = '24px',
    ...otherProps
}) => {
    // Generar el gradiente basado en el color base
    const getGradient = (baseColor) => {
        // Si ya es un gradiente, usarlo tal como está
        if (baseColor.includes('linear-gradient')) {
            return baseColor;
        }

        // Mapeo de colores predefinidos a gradientes
        const gradientMap = {
            '#253289': 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
            '#0F1960': 'linear-gradient(135deg, #0F1960 0%, #3949ab 100%)',
            '#ff4d4f': 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
            '#52c41a': 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            '#1890ff': 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)'
        };

        return gradientMap[baseColor] || `linear-gradient(135deg, ${baseColor} 0%, ${baseColor} 100%)`;
    };

    const modalStyles = {
        content: {
            padding: 0,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
        },
        body: {
            padding: 0,
            ...(maxHeight && {
                maxHeight: maxHeight,
                overflowY: 'auto'
            })
        }
    };

    return (
        <Modal
            title={null}
            open={open}
            onCancel={onCancel}
            footer={footer}
            width={width}
            centered={centered}
            styles={modalStyles}
            closable={closable}
            maskClosable={maskClosable}
            style={{
                top: centered ? undefined : 20,
                ...style
            }}
            {...otherProps}
        >
            {/* Header del modal */}
            {(title || icon) && (
                <div style={{
                    background: getGradient(headerColor),
                    borderRadius: '12px 12px 0 0',
                    padding: '24px',
                    color: 'white',
                    textAlign: 'center',
                    flexShrink: 0
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px'
                    }}>
                        {icon && (
                            <div style={{
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                width: '48px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                            }}>
                                {icon}
                            </div>
                        )}
                        {title && (
                            <div style={{ textAlign: 'left' }}>
                                <Typography.Title level={3} style={{
                                    color: 'white',
                                    margin: 0,
                                    fontSize: '20px'
                                }}>
                                    {title}
                                </Typography.Title>
                                {subtitle && (
                                    <Typography.Text style={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '14px',
                                        display: 'block',
                                        marginTop: '4px'
                                    }}>
                                        {subtitle}
                                    </Typography.Text>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Contenido del modal */}
            <div style={{
                padding: bodyPadding,
                ...(maxHeight && {
                    maxHeight: `calc(${maxHeight} - 120px)`,
                    overflowY: 'auto'
                })
            }}>
                {children}
            </div>
        </Modal>
    );
};

export default CustomModal;